import React, {useState, useMemo} from 'react';
import {Col, Row, Modal} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import useData from '@/utils/useData';
import Spinner from '@/components/ui/Spinner';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import SearchBox from '@/components/search/SearchBox';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import SelectField, {SelectOption} from '@/components/forms/fields/SelectField';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {isModuleEnabled} from '@/utils/modules';

interface AuditLog {
    id: number;
    timestamp: string;
    action_type: string;
    action_description: string;
    actor?: {
        id: number;
        username: string;
    };
    target: {
        role_id?: number;
        user_id?: number;
        permission_id?: number;
    };
    details: Record<string, any>;
    context: {
        user_agent?: string;
        session_id?: string;
    };
}

interface AuditLogsResponse {
    audit_logs: AuditLog[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}


const ACTION_ICONS: Record<string, string> = {
    'role_created': 'mdi-plus-circle',
    'role_updated': 'mdi-pencil-circle',
    'role_deleted': 'mdi-delete-circle',
    'role_cloned': 'mdi-content-duplicate',
    'role_cleared': 'mdi-broom',
    'role_permission_added': 'mdi-shield-plus',
    'role_permission_removed': 'mdi-shield-minus',
    'role_permission_replaced': 'mdi-swap-horizontal',
    'role_user_added': 'mdi-account-plus',
    'role_user_removed': 'mdi-account-minus',
    'user_permission_added': 'mdi-key-plus',
    'user_permission_removed': 'mdi-key-minus',
    'permission_updated': 'mdi-pencil',
    'permission_synced': 'mdi-sync',
    'bulk_role_assignment': 'mdi-account-multiple',
    'bulk_permission_assignment': 'mdi-shield-multiple',
    'template_applied': 'mdi-file-document',
    'system_migration': 'mdi-database-import',
    'system_validation': 'mdi-check-circle',
};

const AuditLogView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [actionFilter, setActionFilter] = useState<SelectOption | null>(null);
    const [actorFilter, setActorFilter] = useState<SelectOption | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(50);
    const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);

    const [auditLogsResponse, loadingAuditLogs] = useData<AuditLogsResponse>(window.URLS.adminAuditLogs, {
        storeResolver: 'auditLogs',
    });

    const allAuditLogs = auditLogsResponse?.audit_logs || [];

    const canViewAuditLogs = hasPermission('fs.roles_permissions.audit_log.read', userData?.permissions, true);

    const formatActionName = (action: string): string => {
        if (!action) {return '';}
        const formatted = action.replace(/_/g, ' ');
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    };

    const actions = useMemo(() => {
        const uniqueActions = new Set(allAuditLogs.map((log: AuditLog) => log.action_type).filter(Boolean));
        return Array.from(uniqueActions).sort();
    }, [allAuditLogs]);

    const actors = useMemo((): string[] => {
        const uniqueActors = new Set(allAuditLogs.map((log: AuditLog) => log.actor?.username).filter(Boolean) as string[]);
        return Array.from(uniqueActors).sort();
    }, [allAuditLogs]);

    const actionOptions = useMemo((): SelectOption[] => {
        return actions.map((action: string) => ({
            value: action,
            label: formatActionName(action)
        }));
    }, [actions]);

    const actorOptions = useMemo((): SelectOption[] => {
        return actors.map((actor: string) => ({
            value: actor,
            label: actor
        }));
    }, [actors]);

    const filteredAuditLogs = useMemo(() => {
        return allAuditLogs.filter((log: AuditLog) => {
            // Search filter
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                const matchesSearch =
                    (log.action_type && log.action_type.toLowerCase().includes(query)) ||
                    (log.action_description && log.action_description.toLowerCase().includes(query)) ||
                    (log.actor?.username && log.actor.username.toLowerCase().includes(query)) ||
                    (log.details && JSON.stringify(log.details).toLowerCase().includes(query));

                if (!matchesSearch) {return false;}
            }

            // Action filter
            if (actionFilter && log.action_type !== actionFilter.value) {
                return false;
            }

            // Actor filter
            if (actorFilter && log.actor?.username !== actorFilter.value) {
                return false;
            }

            return true;
        });
    }, [allAuditLogs, searchQuery, actionFilter, actorFilter]);

    const paginatedAuditLogs = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredAuditLogs.slice(startIndex, endIndex);
    }, [filteredAuditLogs, currentPage, itemsPerPage]);

    const getActionDisplay = (log: AuditLog): string => {
        const action = log.action_type;
        const description = log.action_description;

        if (!action) {return '';}

        // Prefer explicit backend description when it's meaningful
        if (description && description !== action) {
            return description;
        }

        // Special-case for desired casing
        if (action === 'role_cloned') {
            return 'Role cloned';
        }

        // Generic fallback (e.g. "role_deleted" -> "Role Deleted")
        return formatActionName(action);
    };

    const totalPages = Math.ceil(filteredAuditLogs.length / itemsPerPage);
    const pages = Array.from({length: totalPages}, (_, i) => i + 1);

    const handleShowDetails = (log: AuditLog): void => {
        setSelectedLog(log);
        setShowModal(true);
    };

    const handleCloseModal = (): void => {
        setShowModal(false);
        setSelectedLog(null);
    };

    const handlePageChange = (page: number): void => {
        setCurrentPage(page);
    };

    // Reset to first page when filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, actionFilter, actorFilter]);

    const getTargetDisplay = (log: AuditLog): string => {
        const target = log.target;
        if (!target) {return '-';}

        if (target.role_id) {return i18n.t('audit_log_role_id.text', {id: target.role_id});}
        if (target.user_id) {return i18n.t('audit_log_user_id.text', {id: target.user_id});}
        if (target.permission_id) {return i18n.t('audit_log_permission_id.text', {id: target.permission_id});}

        return '-';
    };

    if (loadingUserData) {
        return <Spinner text={i18n.t('sp_loading.text')} centered fullHeight />;
    }

    if (!canViewAuditLogs && !loadingUserData) {
        return (
            <>
                <ConditionalNav />
                <HelpTextBlock helpText={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')} />
                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const columns = [
        {
            header: i18n.t('audit_log_timestamp_header.text'),
            accessorKey: 'timestamp',
            cell: ({getValue}: {getValue: () => string}) => {
                const timestamp = getValue();
                return configLoaded ? formatDateTime(timestamp, true, {setZone: false}) : timestamp;
            },
        },
        {
            header: i18n.t('audit_log_action_header.text'),
            accessorKey: 'action_description',
            cell: ({row}: {row: {original: AuditLog}}) => {
                const log = row.original;
                const action = log.action_type;

                if (!action) {return null;}

                const icon = ACTION_ICONS[action] || 'mdi-information';
                const displayText = getActionDisplay(log);

                return (
                    <div className="d-flex align-items-center" title={displayText}>
                        <Icon name={icon} className="mr-2 text-info" />
                        <span>{displayText}</span>
                    </div>
                );
            },
        },
        {
            header: i18n.t('audit_log_actor_header.text'),
            accessorFn: (row: AuditLog) => row.actor?.username || 'System',
            cell: ({row}: {row: {original: AuditLog}}) => {
                const log = row.original;
                const actor = log.actor;

                if (!actor) {
                    return <span className="text-muted">{i18n.t('audit_log_system.text')}</span>;
                }

                return (
                    <div>
                        <div className="fw-bold">{actor.username}</div>
                        <small className="text-muted">{i18n.t('audit_log_id_label.text', {id: actor.id})}</small>
                    </div>
                );
            },
        },
        {
            header: i18n.t('audit_log_target_header.text'),
            accessorFn: (row: AuditLog) => getTargetDisplay(row),
            cell: ({row}: {row: {original: AuditLog}}) => {
                const log = row.original;
                const target = log.target;

                if (!target || (!target.role_id && !target.user_id && !target.permission_id)) {
                    return <span className="text-muted">-</span>;
                }

                return (
                    <div>
                        {target.role_id && (
                            <div><small className="text-muted">{i18n.t('audit_log_role_label.text')}:</small> {target.role_id}</div>
                        )}
                        {target.user_id && (
                            <div><small className="text-muted">{i18n.t('audit_log_user_label.text')}:</small> {target.user_id}</div>
                        )}
                        {target.permission_id && (
                            <div><small className="text-muted">{i18n.t('audit_log_permission_label.text')}:</small> {target.permission_id}</div>
                        )}
                    </div>
                );
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: ({row}: {row: {original: AuditLog}}) => {
                const log = row.original;
                return (
                    <div className="d-inline-block">
                        <Button
                            variant="outline-info"
                            size="sm"
                            icon="mdi-dots-horizontal"
                            onClick={() => handleShowDetails(log)}
                            title={i18n.t('audit_log_view_details.text')}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <ConditionalNav />

            <HelpTextBlock helpText={i18n.t('audit_log_help_text.text')} />

            {!isModuleEnabled(userData, 'addon-advanced-user-roles') && (
                <div className="alert alert-warning">
                    {i18n.t('roles_advanced_user_roles_module_not_activated.text')}
                </div>
            )}

            <Row className="mb-3">
                <Col lg={3}>
                    <SearchBox
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={i18n.t('audit_log_search_placeholder.text')}
                    />
                </Col>
                <Col lg={2}>
                    <SelectField
                        name="actionFilter"
                        value={actionFilter}
                        onChange={setActionFilter}
                        options={actionOptions}
                        placeholder={i18n.t('audit_log_all_actions.text')}
                        isClearable={true}
                        isSearchable={false}
                    />
                </Col>
                <Col lg={2}>
                    <SelectField
                        name="actorFilter"
                        value={actorFilter}
                        onChange={setActorFilter}
                        options={actorOptions}
                        placeholder={i18n.t('audit_log_all_users.text')}
                        isClearable={true}
                        isSearchable={false}
                    />
                </Col>
                <Col lg={5} className="d-flex justify-content-end gap-2">
                    <div className="text-muted small align-self-center">
                        {i18n.t('audit_log_showing_logs.text', {
                            showing: Math.min(itemsPerPage, filteredAuditLogs.length - (currentPage - 1) * itemsPerPage),
                            total: filteredAuditLogs.length
                        })}
                        {filteredAuditLogs.length !== allAuditLogs.length && i18n.t('audit_log_filtered_from.text', {total: allAuditLogs.length})}
                    </div>
                </Col>
            </Row>

            {loadingAuditLogs ? (
                <Spinner size="sm" text={i18n.t('sp_loading.text')} centered />
            ) : (
                <>
                    <div className="card">
                        <div className="card-body">
                            <TableComponent data={paginatedAuditLogs as AuditLog[]} columns={columns} />

                            <PaginationComponent
                                pages={pages}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                centered={true}
                                hideWhenSinglePage={true}
                            />
                        </div>
                    </div>
                </>
            )}

            {/* Details Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{i18n.t('audit_log_details_title.text')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedLog && (
                        <Row>
                            <Col md={6}>
                                <h6>{i18n.t('audit_log_basic_information.text')}</h6>
                                <ul className="list-unstyled small">
                                    <li><strong>{i18n.t('audit_log_timestamp.text')}:</strong> {configLoaded ? formatDateTime(selectedLog.timestamp, true, {setZone: false}) : selectedLog.timestamp}</li>
                                    <li><strong>{i18n.t('audit_log_action_type.text')}:</strong> {selectedLog.action_type}</li>
                                    <li><strong>{i18n.t('audit_log_action_description.text')}:</strong> {selectedLog.action_description}</li>
                                    <li><strong>{i18n.t('audit_log_actor.text')}:</strong> {selectedLog.actor ? `${selectedLog.actor.username} (${i18n.t('audit_log_id_label.text', {id: selectedLog.actor.id})})` : i18n.t('audit_log_system.text')}</li>
                                </ul>

                                <h6 className="mt-3">{i18n.t('audit_log_target_information.text')}</h6>
                                <ul className="list-unstyled small">
                                    {selectedLog.target?.role_id && <li><strong>{i18n.t('audit_log_role_id_label.text')}:</strong> {selectedLog.target.role_id}</li>}
                                    {selectedLog.target?.user_id && <li><strong>{i18n.t('audit_log_user_id_label.text')}:</strong> {selectedLog.target.user_id}</li>}
                                    {selectedLog.target?.permission_id && <li><strong>{i18n.t('audit_log_permission_id_label.text')}:</strong> {selectedLog.target.permission_id}</li>}
                                    {!selectedLog.target?.role_id && !selectedLog.target?.user_id && !selectedLog.target?.permission_id && (
                                        <li className="text-muted">{i18n.t('audit_log_no_specific_target.text')}</li>
                                    )}
                                </ul>

                                <h6 className="mt-3">{i18n.t('audit_log_context_details.text')}</h6>
                                <ul className="list-unstyled small">
                                    <li><strong>{i18n.t('audit_log_session_id.text')}:</strong> {selectedLog.context?.session_id || i18n.t('audit_log_na.text')}</li>
                                    <li><strong>{i18n.t('audit_log_user_agent.text')}:</strong> <span className="text-muted">{selectedLog.context?.user_agent || i18n.t('audit_log_na.text')}</span></li>
                                </ul>
                            </Col>
                            <Col md={6}>
                                <h6>{i18n.t('audit_log_action_details.text')}</h6>
                                <pre className="small bg-light p-3 rounded" style={{maxHeight: '400px', overflow: 'auto'}}>
                                    {JSON.stringify(selectedLog.details, null, 2)}
                                </pre>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        {i18n.t('jt_close.text')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
});

export default AuditLogView;
